/**
 * Lightweight markdown -> JSX renderer scoped to what our exec /
 * topic-section generator produces: ## / ### / # headings, paragraphs,
 * `- ` bullet lists, **bold**. Extracted from SummaryCard so the
 * shared-summary page can render the same content the same way.
 */

import React from 'react';

function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const re = /\*\*(.+?)\*\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(
      <strong key={`${keyPrefix}-b-${i++}`} className="font-semibold text-gray-900">
        {m[1]}
      </strong>,
    );
    last = re.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export const ExecMarkdown: React.FC<{ markdown: string }> = ({ markdown }) => {
  const blocks: React.ReactNode[] = [];
  const lines = markdown.split('\n');
  let para: string[] = [];
  let bullets: string[] = [];

  const flushPara = (keyHint: number) => {
    if (para.length === 0) return;
    const text = para.join(' ').trim();
    if (text) {
      blocks.push(
        <p key={`p-${keyHint}-${blocks.length}`} className="mt-3 text-gray-800 leading-relaxed">
          {renderInline(text, `p${keyHint}`)}
        </p>,
      );
    }
    para = [];
  };

  const flushBullets = (keyHint: number) => {
    if (bullets.length === 0) return;
    blocks.push(
      <ul key={`ul-${keyHint}-${blocks.length}`} className="mt-2 ml-5 list-disc text-gray-800 leading-relaxed">
        {bullets.map((b, i) => (
          <li key={`b-${keyHint}-${i}`} className="mt-1">
            {renderInline(b, `b${keyHint}-${i}`)}
          </li>
        ))}
      </ul>,
    );
    bullets = [];
  };

  lines.forEach((raw, i) => {
    const line = raw.trimEnd();
    if (line.startsWith('## ')) {
      flushPara(i);
      flushBullets(i);
      blocks.push(
        <h4 key={`h-${i}`} className="mt-5 mb-1 text-base font-semibold text-gray-900">
          {renderInline(line.replace(/^##\s+/, ''), `h${i}`)}
        </h4>,
      );
    } else if (line.startsWith('### ')) {
      flushPara(i);
      flushBullets(i);
      blocks.push(
        <h5 key={`h-${i}`} className="mt-4 mb-1 text-sm font-semibold text-gray-900">
          {renderInline(line.replace(/^###\s+/, ''), `h${i}`)}
        </h5>,
      );
    } else if (line.startsWith('# ')) {
      flushPara(i);
      flushBullets(i);
      blocks.push(
        <h3 key={`h-${i}`} className="mt-5 mb-1 text-lg font-semibold text-gray-900">
          {renderInline(line.replace(/^#\s+/, ''), `h${i}`)}
        </h3>,
      );
    } else if (/^[-*]\s+/.test(line)) {
      flushPara(i);
      bullets.push(line.replace(/^[-*]\s+/, ''));
    } else if (line.trim() === '') {
      flushPara(i);
      flushBullets(i);
    } else {
      flushBullets(i);
      para.push(line);
    }
  });
  flushPara(lines.length);
  flushBullets(lines.length);
  return <div className="text-sm">{blocks}</div>;
};
